import React, { useState, useEffect, useMemo } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { Tabs, Tab } from "react-bootstrap";
import styles from "./RemediationCosts.module.css";

interface TnsClientCost {
  acctId: string;
  accountName: string;
  legalEntityName: string;
  legalEntityId: number;
  phoneNumberCount: number;
  tierCost: number;
  tierLabel: string;
}

interface TnsSummary {
  month: string;
  baseFee: number;
  totalClientFees: number;
  totalCost: number;
  clientCount: number;
  totalPhoneNumbers: number;
  clients: TnsClientCost[];
}

interface TnsTrend {
  month: string;
  baseFee: number;
  totalClientFees: number;
  totalCost: number;
  clientCount: number;
  totalPhoneNumbers: number;
}

const formatCurrency = (value: number) =>
  "$" +
  value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatCurrencyFull = (value: number) =>
  "$" +
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatNumber = (value: number) => (value ?? 0).toLocaleString("en-US");

// Hiya monthly usage shapes (mirror HiyaUsageSummaryDTO / HiyaUsageMonthlyDTO on the backend)
interface HiyaOperationCount {
  operationType: string;
  count: number;
}

interface HiyaUsageSummary {
  month: string;
  billedCount: number; // CheckReputation 200 + reputationAvailable='available' — the invoice number
  reputationAvailable: number;
  reputationNotFound: number;
  reputationUnavailable: number;
  checkReputationTotal: number;
  errorCount: number;
  operations: HiyaOperationCount[];
}

interface HiyaUsageTrend {
  month: string;
  billedCount: number;
}

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.customTooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      <p className={styles.tooltipValue}>{formatCurrency(payload[0].value)}</p>
      <p>{payload[0].payload.phoneNumberCount} phones</p>
    </div>
  );
};

const CustomLineTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.customTooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      <p className={styles.tooltipValue}>{formatCurrency(payload[0].value)}</p>
    </div>
  );
};

const CurrencyCell: React.FC<GridCellProps> = ({ dataItem, field }) => (
  <td style={{ textAlign: "right" }}>{formatCurrencyFull(dataItem[field!])}</td>
);

const NumberCell: React.FC<GridCellProps> = ({ dataItem, field }) => (
  <td style={{ textAlign: "right" }}>{dataItem[field!].toLocaleString()}</td>
);

const TnsCostsTab: React.FC = () => {
  const [month, setMonth] = useState(getCurrentMonth());
  const [summary, setSummary] = useState<TnsSummary | null>(null);
  const [trend, setTrend] = useState<TnsTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilter);

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const trendRes = await callApi(
          "sysadmin/remediation-costs/tns/trend?months=6",
          HttpMethod.GET,
        );
        setTrend(trendRes);
      } catch (err: any) {
        console.error("Failed to load trend data:", err);
      }
    };
    fetchTrend();
  }, []);

  useEffect(() => {
    if (!month) {
      setIsLoading(false);
      setSummary(null);
      return;
    }
    const fetchSummary = async () => {
      setIsLoading(true);
      setError("");
      try {
        const summaryRes = await callApi(
          `sysadmin/remediation-costs/tns?month=${month}`,
          HttpMethod.GET,
        );
        setSummary(summaryRes);
      } catch (err: any) {
        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSummary();
  }, [month]);

  const barData = useMemo(() => {
    if (!summary?.clients) return [];
    return [...summary.clients]
      .sort((a, b) => b.tierCost - a.tierCost)
      .slice(0, 15)
      .map((c) => ({
        name:
          c.accountName?.length > 20
            ? c.accountName.substring(0, 18) + "..."
            : c.accountName || c.acctId,
        tierCost: c.tierCost,
        phoneNumberCount: c.phoneNumberCount,
      }));
  }, [summary]);

  const filteredClients = useMemo(() => {
    if (!summary?.clients) return [];
    if (filter?.filters?.length > 0) {
      return filterBy(summary.clients, filter);
    }
    return summary.clients;
  }, [summary, filter]);

  const TotalFooterCell = () => {
    const total = filteredClients.reduce((sum, c) => sum + c.tierCost, 0);
    return (
      <td style={{ fontWeight: "bold", textAlign: "right" }}>
        {formatCurrencyFull(total)}
      </td>
    );
  };

  const PhoneTotalFooterCell = () => {
    const total = filteredClients.reduce(
      (sum, c) => sum + c.phoneNumberCount,
      0,
    );
    return (
      <td style={{ fontWeight: "bold", textAlign: "right" }}>
        {total.toLocaleString()}
      </td>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingOverlay}>
          <span className={styles.spinner} />
          Loading TNS cost data...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <label htmlFor="month-picker">Month</label>
        <input
          id="month-picker"
          type="month"
          className={styles.monthInput}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {summary && (
        <>
          {/* Stat Cards */}
          <div className={styles.statsRow}>
            <div className={`${styles.statCard} ${styles.statCardTotal}`}>
              <div className={styles.statLabel}>Total Cost</div>
              <div className={styles.statValue}>
                {formatCurrency(summary.totalCost)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardBase}`}>
              <div className={styles.statLabel}>Base Fee</div>
              <div className={styles.statValue}>
                {formatCurrency(summary.baseFee)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardClient}`}>
              <div className={styles.statLabel}>Client Fees</div>
              <div className={styles.statValue}>
                {formatCurrency(summary.totalClientFees)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardClients}`}>
              <div className={styles.statLabel}>Clients</div>
              <div className={`${styles.statValue} ${styles.statValueSmall}`}>
                {summary.clientCount}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardPhones}`}>
              <div className={styles.statLabel}>Phone Numbers</div>
              <div className={`${styles.statValue} ${styles.statValueSmall}`}>
                {summary.totalPhoneNumbers.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className={styles.chartsRow}>
            <div className={styles.chartPanel}>
              <div className={styles.chartTitle}>Top Clients by Cost</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={barData}
                  margin={{ top: 4, right: 12, bottom: 40, left: 12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ebee" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#697077" }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#697077" }}
                    tickFormatter={(v) => `$${v.toLocaleString()}`}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar
                    dataKey="tierCost"
                    fill="#0f62fe"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className={styles.chartPanel}>
              <div className={styles.chartTitle}>Monthly Cost Trend</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={trend}
                  margin={{ top: 4, right: 12, bottom: 4, left: 12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ebee" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "#697077" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#697077" }}
                    tickFormatter={(v) => `$${v.toLocaleString()}`}
                  />
                  <Tooltip content={<CustomLineTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    stroke="#0f62fe"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#0f62fe", strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: "#0353e9" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Data Table */}
          <div className={styles.gridSection}>
            <div className={styles.gridHeader}>
              <span className={styles.gridTitle}>Client Breakdown</span>
              <span className={styles.clientCount}>
                {filteredClients.length} client
                {filteredClients.length !== 1 ? "s" : ""}
              </span>
            </div>
            <Grid
              data={filteredClients}
              sortable={true}
              filterable={true}
              resizable={true}
              filter={filter}
              onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
              style={{ height: "calc(100vh - 680px)", minHeight: "300px" }}
            >
              <Column
                field="accountName"
                title="Account Name"
                width={220}
                footerCell={() => (
                  <td style={{ fontWeight: "bold" }}>Total:</td>
                )}
              />
              <Column field="acctId" title="Account ID" width={130} />
              <Column
                field="legalEntityName"
                title="Legal Entity"
                width={220}
              />
              <Column
                field="phoneNumberCount"
                title="Phone Numbers"
                filter="numeric"
                width={140}
                cells={{ data: NumberCell }}
                footerCell={PhoneTotalFooterCell}
              />
              <Column field="tierLabel" title="Tier" width={110} />
              <Column
                field="tierCost"
                title="Cost"
                filter="numeric"
                width={120}
                cells={{ data: CurrencyCell }}
                footerCell={TotalFooterCell}
              />
            </Grid>
          </div>
        </>
      )}
    </div>
  );
};

const HiyaUsageTab: React.FC = () => {
  const [month, setMonth] = useState(getCurrentMonth());
  const [summary, setSummary] = useState<HiyaUsageSummary | null>(null);
  const [trend, setTrend] = useState<HiyaUsageTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const trendRes = await callApi(
          "sysadmin/remediation-costs/hiya/trend?months=6",
          HttpMethod.GET,
        );
        setTrend(trendRes);
      } catch (err: any) {
        console.error("Failed to load Hiya trend data:", err);
      }
    };
    fetchTrend();
  }, []);

  useEffect(() => {
    if (!month) {
      setIsLoading(false);
      setSummary(null);
      return;
    }
    const fetchSummary = async () => {
      setIsLoading(true);
      setError("");
      try {
        const summaryRes = await callApi(
          `sysadmin/remediation-costs/hiya?month=${month}`,
          HttpMethod.GET,
        );
        setSummary(summaryRes);
      } catch (err: any) {
        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSummary();
  }, [month]);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingOverlay}>
          <span className={styles.spinner} />
          Loading Hiya usage data...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <label htmlFor="hiya-month-picker">Month</label>
        <input
          id="hiya-month-picker"
          type="month"
          className={styles.monthInput}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {summary && (
        <>
          {/* Stat Cards — Billed (Available) is the figure that goes on the Hiya invoice */}
          <div className={styles.statsRow}>
            <div className={`${styles.statCard} ${styles.statCardTotal}`}>
              <div className={styles.statLabel}>Billed (Available)</div>
              <div className={styles.statValue}>
                {formatNumber(summary.billedCount)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardBase}`}>
              <div className={styles.statLabel}>Not Found</div>
              <div className={styles.statValue}>
                {formatNumber(summary.reputationNotFound)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardClient}`}>
              <div className={styles.statLabel}>Unavailable</div>
              <div className={styles.statValue}>
                {formatNumber(summary.reputationUnavailable)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardClients}`}>
              <div className={styles.statLabel}>Total Reputation Checks</div>
              <div className={`${styles.statValue} ${styles.statValueSmall}`}>
                {formatNumber(summary.checkReputationTotal)}
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.statCardPhones}`}>
              <div className={styles.statLabel}>Errors</div>
              <div className={`${styles.statValue} ${styles.statValueSmall}`}>
                {formatNumber(summary.errorCount)}
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className={styles.chartsRow}>
            <div className={styles.chartPanel}>
              <div className={styles.chartTitle}>Calls by Operation Type</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={summary.operations}
                  margin={{ top: 4, right: 12, bottom: 40, left: 12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ebee" />
                  <XAxis
                    dataKey="operationType"
                    tick={{ fontSize: 10, fill: "#697077" }}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#697077" }}
                    tickFormatter={(v) => v.toLocaleString()}
                  />
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                  <Bar dataKey="count" fill="#0f62fe" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className={styles.chartPanel}>
              <div className={styles.chartTitle}>Billed Usage Trend</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={trend}
                  margin={{ top: 4, right: 12, bottom: 4, left: 12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ebee" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "#697077" }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#697077" }}
                    tickFormatter={(v) => v.toLocaleString()}
                  />
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                  <Line
                    type="monotone"
                    dataKey="billedCount"
                    stroke="#0f62fe"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#0f62fe", strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: "#0353e9" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Operation breakdown table */}
          <div className={styles.gridSection}>
            <div className={styles.gridHeader}>
              <span className={styles.gridTitle}>Operation Breakdown</span>
              <span className={styles.clientCount}>
                {summary.operations.length} operation
                {summary.operations.length !== 1 ? "s" : ""}
              </span>
            </div>
            <Grid
              data={summary.operations}
              sortable={true}
              resizable={true}
              style={{ height: "calc(100vh - 680px)", minHeight: "300px" }}
            >
              <Column field="operationType" title="Operation Type" width={260} />
              <Column
                field="count"
                title="Calls"
                filter="numeric"
                cells={{ data: NumberCell }}
              />
            </Grid>
          </div>
        </>
      )}
    </div>
  );
};

const RemediationCosts: React.FC = () => (
  <Tabs defaultActiveKey="tns" className={styles.tabNav} mountOnEnter>
    <Tab eventKey="tns" title="TNS Costs">
      <TnsCostsTab />
    </Tab>
    <Tab eventKey="hiya" title="Hiya Usage">
      <HiyaUsageTab />
    </Tab>
  </Tabs>
);

export default RemediationCosts;
